import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { readonly, ref } from 'vue'
import type {
  AiModel,
  AiQueueItem,
  ApiResponse,
  ModelConfirmRequest,
  PaginatedResponse,
  ProcessingError,
  QueueListParams,
  UploadRequest,
  UploadResponse,
} from '../types'

/**
 * API composable for AI document processing
 * Handles all HTTP requests to the ai-processing endpoints
 */
export function useAiDocumentApi() {
  // Reactive state
  const loading = ref(false)
  const error = ref<ProcessingError | null>(null)

  /**
   * Clear error state
   */
  const clearError = (): void => {
    error.value = null
  }

  /**
   * Handle API errors consistently
   */
  const handleError = (err: any): void => {
    console.error('AI Document API Error:', err)

    error.value = {
      code: err.response?.status?.toString() || 'UNKNOWN',
      message: err.response?.data?.message || err.message || 'An unknown error occurred',
      details: err.response?.data || {},
      timestamp: new Date().toISOString(),
    }
  }

  /**
   * Execute API request with loading state and error handling
   */
  const executeRequest = async <T>(requestFn: () => Promise<T>): Promise<T | null> => {
    try {
      loading.value = true
      clearError()

      const result = await requestFn()
      return result
    } catch (err) {
      handleError(err)
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Upload files for AI processing
   */
  const uploadAndProcess = async (uploadData: UploadRequest): Promise<UploadResponse | null> => {
    return executeRequest(async () => {
      const formData = new FormData()

      // Add files to form data
      uploadData.files.forEach((file, index) => {
        formData.append(`files[${index}]`, file)
      })

      // Add optional parameters
      if (uploadData.target_model_class) {
        formData.append('target_model_class', uploadData.target_model_class)
      }
      if (uploadData.model_config_key) {
        formData.append('model_config_key', uploadData.model_config_key)
      }
      if (uploadData.priority) {
        formData.append('priority', uploadData.priority)
      }

      const response = await axiosApi.post<ApiResponse<UploadResponse>>('/ai-processing/queue/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      return response.data.data
    })
  }

  /**
   * Get available AI models for processing
   */
  const getAvailableModels = async (): Promise<AiModel[] | null> => {
    return executeRequest(async () => {
      const response = await axiosApi.get<ApiResponse<AiModel[]>>('/ai-processing/models')
      return response.data.data
    })
  }

  /**
   * Get processing queue items with server-side filters and global status counts
   */
  const getQueue = async ({ page = 1, perPage = 20, status = 'all', priority = 'all', search = '' }: QueueListParams = {}): Promise<
    PaginatedResponse<AiQueueItem> | null
  > => {
    return executeRequest(async () => {
      const params: Record<string, string | number> = { page, per_page: perPage }
      if (status !== 'all') params.status = status
      if (priority !== 'all') params.priority = priority
      if (search.trim()) params.search = search.trim()

      const response = await axiosApi.get('/ai-processing/queue', { params })
      const { data, pagination, meta } = response.data
      return {
        data,
        current_page: pagination.current_page,
        per_page: pagination.per_page,
        total: pagination.total,
        last_page: pagination.last_page,
        status_counts: meta?.status_counts,
      }
    })
  }

  /**
   * Get specific queue item status
   */
  const getQueueStatus = async (queueId: string): Promise<AiQueueItem | null> => {
    return executeRequest(async () => {
      const response = await axiosApi.get<ApiResponse<AiQueueItem>>(`/ai-processing/queue/${queueId}`)
      return response.data.data
    })
  }

  /**
   * Confirm model type for queue item
   */
  const confirmModelType = async (queueId: string, modelType: string): Promise<boolean> => {
    const result = await executeRequest(async () => {
      const requestData: ModelConfirmRequest = { model_type: modelType }

      await axiosApi.post<ApiResponse<any>>(`/ai-processing/queue/${queueId}/confirm`, requestData)
      return true
    })

    return result !== null
  }

  /**
   * Cancel processing for queue item
   */
  const cancelProcessing = async (queueId: string): Promise<boolean> => {
    const result = await executeRequest(async () => {
      await axiosApi.post<ApiResponse<any>>(`/ai-processing/queue/${queueId}/cancel`)
      return true
    })

    return result !== null
  }

  /**
   * Retry failed processing
   */
  const retryProcessing = async (queueId: string): Promise<boolean> => {
    const result = await executeRequest(async () => {
      await axiosApi.post<ApiResponse<any>>(`/ai-processing/queue/${queueId}/retry`)
      return true
    })

    return result !== null
  }

  /**
   * Delete a finished queue item (record + uploaded files)
   */
  const deleteQueue = async (queueId: string): Promise<boolean> => {
    const result = await executeRequest(async () => {
      await axiosApi.delete<ApiResponse<any>>(`/ai-processing/queue/${queueId}`)
      return true
    })

    return result !== null
  }

  /**
   * Bulk cancel queue items
   */
  const bulkCancel = async (queueIds: string[]): Promise<boolean> => {
    const result = await executeRequest(async () => {
      await axiosApi.post<ApiResponse<any>>('/ai-processing/queue/bulk-cancel', {
        queue_ids: queueIds,
      })
      return true
    })

    return result !== null
  }

  const bulkRetry = async (queueIds: string[]): Promise<boolean> => {
    const result = await executeRequest(async () => {
      await axiosApi.post<ApiResponse<any>>('/ai-processing/queue/bulk-retry', {
        queue_ids: queueIds,
      })
      return true
    })

    return result !== null
  }

  // Return readonly state and actions
  return {
    // State (readonly)
    loading: readonly(loading),
    error: readonly(error),

    // Actions
    clearError,
    uploadAndProcess,
    getAvailableModels,
    getQueue,
    getQueueStatus,
    confirmModelType,
    cancelProcessing,
    retryProcessing,
    deleteQueue,
    bulkCancel,
    bulkRetry,
  } as const
}
